/**
 * `VlaStatusTypeEnum` code -> known `statusDescription` string(s) as returned
 * by `ApplicationStatusApi.getApplicationStatus`. Business-confirmed code
 * catalog (step6-permission-matrix.md §3), cross-checked against real
 * strings already observed elsewhere in this app.
 *
 * NOTE: the business-confirmed table gives `AP` = "Approval", but this app's
 * existing `isPreApprovalApp` predicate (and its tests, moved into this same
 * folder) uses "Approval Pending" for the same state. Both spellings are
 * listed as aliases below until confirmed which one `statusDescription`
 * actually returns for `AP` — matching either is safe since no other code
 * claims either string. Similarly `CC`'s business nickname is "Fund"; the
 * one real `statusDescription` seen in this app for a non-pre-approval deal
 * is "Booked", so both are listed.
 */
export type DealStatusCode = "AP" | "PD" | "PC" | "CP" | "AH" | "CC" | "RAR" | "RAC" | "CR" | "RAP" | "PS" | "RR";

export const STATUS_DESCRIPTIONS_BY_CODE: Record<DealStatusCode, readonly string[]> = {
  AP: ["Approval", "Approval Pending"],
  PD: ["Pending Deal"],
  PC: ["Pending Contract"],
  CP: ["Contract Processing"],
  AH: ["Advance on Hold"],
  CC: ["Contract Completed", "Booked"],
  RAR: ["Re-assignment Request"],
  RAC: ["Re-assignment Completed"],
  CR: ["Contract Received"],
  RAP: ["Re-assignment Pending"],
  PS: ["Pending Signatures"],
  RR: ["Ready for Review"],
};

/**
 * `WorkQueueTypePhase` description strings (matrix §3). Unlike status codes,
 * phases have no separate short code — the description string is the only
 * identifier, matching how legacy code compares
 * `WorkQueueTypePhase.getDescription()` directly against literals.
 */
export const KNOWN_PHASE_DESCRIPTIONS = [
  "Pre-approval Requested",
  "Pre-approval Pending",
  "Pre-approval Completed",
  "New Contract Required",
  "Analyst Review",
  "Completed",
] as const;

/**
 * Builds a lookup Set of every known `statusDescription` alias for the given
 * codes, for use in `Set.has(statusDescription)` membership checks.
 * @param {DealStatusCode[]} codes - The status codes to include.
 * @returns {Set<string>} The union of all description aliases for those codes.
 */
export function descriptionsForCodes(...codes: DealStatusCode[]): Set<string> {
  return new Set(codes.flatMap((code) => STATUS_DESCRIPTIONS_BY_CODE[code]));
}
